import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { COLORS } from '../../theme/tokens';
import { Root, type ButtonVariant } from './Button.styles';

export type { ButtonVariant };

export type ButtonProps = {
  label: string;
  variant?: ButtonVariant;
  icon?: IconName;
  fullWidth?: boolean;
  /**
   * Drop the visible copy and render a square icon-only control (a header
   * preview/settings button, a row's kebab). `label` stays required and becomes
   * the accessible name, so the action is still announced and still translated.
   * Requires `icon`.
   */
  iconOnly?: boolean;
  type?: 'button' | 'submit';
  /** A tooltip, as given (translate it first): an icon-only button among others that show theirs. */
  title?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Injected by styled-components when wrapped with styled(Button). */
  className?: string;
};

/** Pill button. `primary` = filled CTA, `outline` = bordered secondary. Label via <Text>. */
export const Button = ({
  label,
  variant = 'primary',
  icon,
  fullWidth = false,
  iconOnly = false,
  type = 'button',
  title,
  disabled = false,
  onClick,
  className,
}: ButtonProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Root
      type={type}
      $variant={variant}
      $fullWidth={fullWidth}
      $iconOnly={iconOnly}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-label={iconOnly ? t(label) : undefined}
    >
      {icon ? (
        <Icon
          name={icon}
          size={iconOnly ? 20 : 18}
          color={variant === 'primary' ? COLORS.ctaSolidText : COLORS.ctaText}
        />
      ) : null}
      {iconOnly ? null : <Text text={label} tag="span" />}
    </Root>
  );
};

export default Button;
